import { Inject, Injectable, RendererFactory2, ViewEncapsulation, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * Attributes of a `<link>` tag.
 */
export declare type LinkDefinition = {
  charset?: string;
  crossorigin?: string;
  href?: string;
  hreflang?: string;
  media?: string;
  rel?: string;
  rev?: string;
  sizes?: string;
  target?: string;
  type?: string;
} & {
  [prop: string]: string;
};

/**
 * A service that can be used to get and add link tags.
 */
@Injectable()
export class LinkService {
  private renderer: Renderer2;

  constructor(@Inject(DOCUMENT) private document, rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(this.document, {
      id: '-1',
      encapsulation: ViewEncapsulation.None,
      styles: [],
      data: {}
    });
  }

  addTag(attributes: LinkDefinition, forceCreation: boolean = false, top: boolean = false) {
    return this.getOrCreateElement(attributes, forceCreation, top);
  }

  getTag(attrSelector: string): HTMLLinkElement | null {
    return this.document.querySelector(`link[${attrSelector}]`) || null;
  }

  addTags(tags: LinkDefinition[], forceCreation: boolean = false): HTMLLinkElement[] {
    return tags.reduce((result: HTMLLinkElement[], attributes: LinkDefinition) => {
      if (attributes) {
        result.push(this.getOrCreateElement(attributes, forceCreation));
      }
      return result;
    }, []);
  }

  getTags(attrSelector: string): HTMLLinkElement[] {
    const list = this.document.querySelectorAll(`link[${attrSelector}]`);
    return list ? [].slice.call(list) : [];
  }

  updateTag(attributes: LinkDefinition, selector?: string): HTMLLinkElement | null {
    selector = selector || this.getAttrSelector(attributes);
    const element = this.getTag(selector);

    if (element) {
      return this.setElementAttributes(element, attributes);
    }
    return this.getOrCreateElement(attributes, true);
  }

  removeTag(attrSelector: string): void {
    this.getTags(attrSelector).forEach((element: HTMLLinkElement) => this.removeTagElement(element));
  }

  removeTagElement(element: HTMLLinkElement): void {
    if (element) {
      const head = this.document.head;
      this.renderer.removeChild(head, element);
    }
  }

  private getOrCreateElement(
    attributes: LinkDefinition,
    forceCreation: boolean = false,
    top: boolean = false
  ): HTMLLinkElement {
    let element: HTMLLinkElement;

    if (!forceCreation) {
      const selector = this.getAttrSelector(attributes);
      element = this.getTag(selector);

      if (element && this.containsAttributes(element, attributes)) {
        return element;
      }
    }
    element = this.renderer.createElement('link');
    this.setElementAttributes(element, attributes);

    const head = this.document.head;
    const topTag = this.document.querySelectorAll('meta[name="referrer"]');

    if (!top) {
      this.renderer.appendChild(head, element);
    } else {
      this.renderer.insertBefore(head, element, topTag[0]);
    }

    return element;
  }

  private setElementAttributes(element: HTMLLinkElement, attributes: LinkDefinition): HTMLLinkElement {
    Object.keys(attributes).forEach((attr: string) => this.renderer.setAttribute(element, attr, attributes[attr]));
    return element;
  }

  private containsAttributes(element: HTMLLinkElement, attributes: LinkDefinition): boolean {
    return Object.keys(attributes).every((attr: string) => element.getAttribute(attr) === attributes[attr]);
  }

  private getAttrSelector(tag: LinkDefinition): string {
    const attr: string = tag.rel ? 'rel' : 'hreflang';
    return `${attr}="${tag[attr]}"`;
  }
}
